/* eslint-disable import/no-unresolved */
/* eslint-disable no-shadow */

import React, { RefAttributes, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { react, useRefWithSetter } from "uikit";

import { Role } from "../../../../../../../../services";
import Map from "../../../../../../../../redux/services/Map";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import useKeepCardsOpen from "../../../../../../../../hooks/useKeepCardsOpen";
import useCardSubscriber from "../../../../../../../../hooks/useCardSubscriber";
import EditModalBase from "../../../../../../../../components/EditModal";

import { Footer, Root, Header, Content } from "./components";
import InternalController from "./Controller";

const EditModal = react.withController<
	EditModal.PropsBase,
	EditModal.Controller
>(({ controller, value, language, onCancel, onSave }) => {
	const [contentRef, setContentRef] = useRefWithSetter<Content.Ref | null>(
		null,
	);

	const { t } = useTranslation();

	const cardIds = useMemo(
		() => (typeof value.id === "number" ? [value.id] : []),
		[value.id],
	);

	useKeepCardsOpen(cardIds, Role.Card);

	const cardSession = useCardSubscriber(Role.Card);

	const currentCard = useMemo(
		() =>
			cardSession?.cards.find((card) => cardIds.includes(card.id)) ??
			null,
		[cardIds, cardSession?.cards],
	);

	controller.setContext({ contentRef });

	const [tab, setTab] = useState<Header.Tab>("main");
	const [internalValue, setInternalValue] = useState(value);

	const valueEditor = useObjectEditor(internalValue, setInternalValue);

	const assignValue = valueEditor.useAssigner();

	const name = valueEditor.useGetter("name");

	const headerTitle = useMemo(
		() => (typeof value.id === "number" ? name : t("newSettings") ?? ""),
		[name, value.id, t],
	);

	const headerLockedBy = useMemo(
		() =>
			currentCard?.locked
				? [
						currentCard.lockedBy.person?.lastName ?? "",
						currentCard.lockedBy.person?.firstName ?? "",
						currentCard.lockedBy.person?.fatherName ?? "",
				  ]
						.join(" ")
						.trim()
				: undefined,
		[currentCard?.locked, currentCard?.lockedBy],
	);

	const modalContent = useMemo(
		() => (
			<Root sizes="auto! 1fr">
				<Header
					tab={tab}
					title={headerTitle}
					lockedBy={headerLockedBy}
					createdAt={value.createdAt}
					onChangeTab={setTab}
				/>
				<Content
					ref={setContentRef}
					disabled={currentCard?.locked === true}
					value={internalValue}
					type={tab}
					language={language}
					onChange={assignValue}
				/>
			</Root>
		),
		[
			assignValue,
			currentCard?.locked,
			headerLockedBy,
			headerTitle,
			internalValue,
			language,
			setContentRef,
			tab,
			value.createdAt,
		],
	);

	const footerValue = valueEditor.usePicker(["active", "default"]);

	const modalFooter = useMemo(
		() => (
			<Footer
				value={footerValue}
				disabled={currentCard?.locked === true}
				onChange={assignValue}
			/>
		),
		[footerValue, currentCard?.locked, assignValue],
	);

	const modalOnSave = useCallback(() => {
		if (!contentRef.current?.validate()) return;

		onSave(valueEditor.value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentRef, onSave]);

	return (
		<EditModalBase
			footer={modalFooter}
			canSave={currentCard?.locked !== true}
			onCancel={onCancel}
			onSave={modalOnSave}
		>
			{modalContent}
		</EditModalBase>
	);
}, InternalController);

declare namespace EditModal {
	type Ref = InternalController | null;

	type Controller = InternalController;

	interface Value extends Content.Value, Footer.Value {
		id?: number;

		name: string;

		createdAt?: string;
	}

	interface PropsBase {
		value: any;
		language: Map.Language;

		onCancel: () => void;
		onSave: (value: any) => void;
	}

	type Props = PropsBase & RefAttributes<Ref>;
}

export default EditModal;
